// ---------------------------------------------------------------------------
// Community guides - the part of the product that is not admin.
//
// The feed and events are the difference between a landlord and an operator.
// They are also the two features that quietly die if nobody is told how they
// work, which is what these two guides are for.
//
// Events sits behind the `events` plan feature as well as its permission codes,
// so the guide carries a featureSlug and drops out of the launcher entirely for
// organizations whose plan does not include it. The feed does not.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const FEED = PATH_ADMIN.community;
const EVENTS = PATH_ADMIN.events.root;
const EVENT_CREATE = PATH_ADMIN.events.create;

export const COMMUNITY_FEED = {
  id: 'community-feed',
  title: 'Run the community feed',
  summary:
    'Post announcements members actually read, and moderate what they post back '
    + 'without becoming the reason nobody posts.',
  category: 'community',
  level: 'advanced',
  permission: ['community.view', 'community.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'community', 'feed', 'post', 'announcement', 'notice', 'moderation',
    'members', 'broadcast', 'noticeboard',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: FEED,
      target: CENTER,
      placement: 'center',
      title: 'The feed is your noticeboard, and theirs',
      body:
        'Members see it in the app and the portal. You use it for the things '
        + 'that used to be an email nobody opened: the lift is out on Thursday, '
        + 'the new coffee machine, drinks on Friday. They use it to reach each '
        + 'other, which is the half that actually makes a community.',
    },
    {
      id: 'post',
      kind: 'act',
      route: FEED,
      target: ANCHOR.communityNewPost,
      placement: 'bottom',
      title: 'Write one',
      body:
        'Post as the space rather than as yourself. Members read an operator '
        + 'post as official, and that is exactly what you want for anything about '
        + 'the building.',
      waitingLabel: 'Click New post.',
      advanceOn: [{ type: 'click', target: ANCHOR.communityNewPost }],
      doItForMe: { type: 'click', target: ANCHOR.communityNewPost },
      allowSkip: true,
    },
    {
      id: 'write',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Short, specific, and with the date in it',
      body:
        'People read this on a phone between meetings. Lead with what changes '
        + 'for them and when. An image gets several times the attention of plain '
        + 'text, so use one whenever there is anything to show.',
      waitingLabel: 'Waiting for the post to be published.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.postPublished }],
      allowSkip: true,
    },
    {
      id: 'moderation',
      kind: 'narrate',
      route: FEED,
      target: CENTER,
      placement: 'center',
      title: 'Moderate lightly, and quickly',
      body:
        'You can review, hide or remove member posts and comments from here. '
        + 'Reach for it rarely. A feed where posts vanish is a feed people stop '
        + 'using, and a dead feed is worse than an untidy one. Deal with the '
        + 'genuinely abusive and let the rest be.',
    },
    {
      id: 'rhythm',
      kind: 'narrate',
      route: FEED,
      target: CENTER,
      placement: 'center',
      title: 'It only works if it is fed',
      body:
        'One or two posts a week is enough, and it has to be steady. A feed with '
        + 'nothing since March tells every new member that nothing happens here. '
        + 'If your organization allows it, member posts can be set to need '
        + 'approval before they appear, which is a reasonable setting for a new '
        + 'space and usually worth relaxing later.',
    },
  ],
};

export const COMMUNITY_EVENTS = {
  id: 'community-events',
  title: 'Run events and challenges',
  summary:
    'Put an event in front of members, see who is coming, and run a scored '
    + 'challenge if you want more than attendance.',
  category: 'community',
  level: 'advanced',
  permission: ['events.view', 'events.manage'],
  featureSlug: 'events',
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'event', 'events', 'challenge', 'rsvp', 'workshop', 'meetup',
    'attendance', 'leaderboard', 'steps',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: EVENTS,
      target: CENTER,
      placement: 'center',
      title: 'Events are the reason people renew',
      body:
        'Members compare your desk price to the one down the road. They cannot '
        + 'compare the people they met at your Thursday session. Everything here '
        + 'shows up in the member app, where people can see it and sign up '
        + 'without asking anyone.',
    },
    {
      id: 'create',
      kind: 'act',
      route: EVENTS,
      target: ANCHOR.eventsCreate,
      placement: 'bottom',
      title: 'Create one',
      body:
        'A talk, a workshop, drinks, or a challenge that runs for a month. They '
        + 'all start from here.',
      waitingLabel: 'Click Create.',
      advanceOn: [
        { type: 'click', target: ANCHOR.eventsCreate },
        { type: 'route', path: EVENT_CREATE },
      ],
      doItForMe: { type: 'click', target: ANCHOR.eventsCreate },
      allowSkip: true,
    },
    {
      id: 'details',
      kind: 'await',
      route: EVENT_CREATE,
      target: CENTER,
      placement: 'center',
      title: 'When, where, and whether it is scored',
      body:
        'Scoring is what separates an event from a challenge. Leave it off and '
        + 'this is an ordinary event people RSVP to. Turn it on and participants '
        + 'get a leaderboard, either from steps synced by the member app or from '
        + 'scores you enter yourself. A step challenge runs for weeks with almost '
        + 'no work from you, which makes it the cheapest community programming '
        + 'there is.',
      waitingLabel: 'Waiting for the event to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.eventCreated }],
      allowSkip: true,
    },
    {
      id: 'enrollment',
      kind: 'narrate',
      route: EVENTS,
      target: CENTER,
      placement: 'center',
      title: 'One record covers signing up and turning up',
      body:
        'A member enrolls once, and the same record is what you mark as attended '
        + 'on the day. So the gap between how many said yes and how many came is '
        + 'visible rather than anecdotal, and after three or four events you know '
        + 'which formats your members actually show up for.',
    },
    {
      id: 'promote',
      kind: 'narrate',
      route: EVENTS,
      target: CENTER,
      placement: 'center',
      title: 'Announce it, then announce it again',
      body:
        'Post it to the community feed when it opens and again the day before. '
        + 'The single biggest cause of an empty room is not a bad idea, it is one '
        + 'announcement three weeks early that everybody forgot.',
    },
  ],
};

export default [COMMUNITY_FEED, COMMUNITY_EVENTS];
